import React, { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GironeBadge from "@/components/GironeBadge";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Flame, Search, X as XIcon } from "lucide-react";

const ROLE_COLORS = { PT: "#f59e0b", DEF: "#06b6d4", CEN: "#3b56e0", ATT: "#f97316" };
const GIRONE_COLORS = { A: "#3b56e0", B: "#f97316", C: "#06b6d4", D: "#a855f7" };

function ratingColor(r) {
  if (r >= 8.5) return "#ea580c";
  if (r >= 7.5) return "#16a34a";
  if (r >= 6.5) return "#0284c7";
  if (r >= 6.0) return "#64748b";
  return "#dc2626";
}

export default function PerformancePage() {
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [gironeFilter, setGironeFilter] = useState("ALL");
  const [selectedPlayer, setSelectedPlayer] = useState(null); // {name, role}
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    api.get("/public/performance/players")
      .then(({ data }) => setPlayers(Array.isArray(data) ? data : []))
      .catch(() => setPlayers([]));
  }, []);

  useEffect(() => {
    if (!selectedPlayer) return;
    setHistoryLoading(true);
    setHistory(null);
    api.get(`/public/performance/player/history`, {
      params: { name: selectedPlayer.name, role: selectedPlayer.role },
    })
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory(null))
      .finally(() => setHistoryLoading(false));
  }, [selectedPlayer]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const gFilter = (gironeFilter || "ALL").toUpperCase();
    // SEMPRE solo voti reali (calciatori realmente giocati e riconosciuti dagli screenshot).
    const filtered = players.filter((p) => {
      if (!p.has_real_data) return false;
      if (roleFilter !== "ALL" && (p.role || "").toUpperCase() !== roleFilter) return false;
      if (gFilter !== "ALL") {
        const inGirone = (p.teams || []).some((t) => (t?.girone || "").toString().trim().toUpperCase() === gFilter);
        if (!inGirone) return false;
      }
      if (s !== "") {
        const inName = p.name?.toLowerCase().includes(s);
        const inTeam = (p.teams || []).some((t) => (t.team_name || "").toLowerCase().includes(s));
        if (!inName && !inTeam) return false;
      }
      return true;
    });
    filtered.sort((a, b) => {
      const dr = (b.rating || 0) - (a.rating || 0);
      if (dr !== 0) return dr;
      const dm = (b.matches_played || 0) - (a.matches_played || 0);
      if (dm !== 0) return dm;
      return (a.name || "").localeCompare(b.name || "");
    });
    return filtered;
  }, [players, q, roleFilter, gironeFilter]);

  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 space-y-6">
        <div className="fade-up flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "#f9731618", color: "#ea580c" }}>
            <Flame className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Voti medi · aggiornati giornata per giornata</div>
            <h1 className="display font-black text-4xl md:text-5xl">Calciatori</h1>
          </div>
        </div>

        <div className="flex flex-col gap-2" data-testid="perf-filters">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca calciatore o squadra…"
                     className="h-10 pl-9 bg-white border-slate-200 rounded-full" data-testid="perf-search" />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {["ALL", "PT", "DEF", "CEN", "ATT"].map((r) => (
                <button key={r} type="button" onClick={() => setRoleFilter(r)}
                        data-testid={`perf-role-${r}`}
                        className={`h-10 px-3 rounded-full text-[11px] font-black uppercase tracking-widest transition
                          ${roleFilter === r ? "text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                        style={roleFilter === r ? { background: r === "ALL" ? "#0f172a" : ROLE_COLORS[r] } : undefined}>
                  {r === "ALL" ? "Tutti" : r}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mr-1">Girone</span>
            {["ALL", "A", "B", "C", "D"].map((gg) => (
              <button key={gg} type="button" onClick={() => setGironeFilter(gg)}
                      data-testid={`perf-girone-${gg}`}
                      className={`h-8 px-3 rounded-full text-[10px] font-black uppercase tracking-widest transition inline-flex items-center gap-1.5
                        ${gironeFilter === gg ? "text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                      style={gironeFilter === gg ? { background: gg === "ALL" ? "#0f172a" : GIRONE_COLORS[gg] } : undefined}>
                {gg !== "ALL" && <GironeBadge girone={gg} size={14} />}
                {gg === "ALL" ? "Tutti" : gg}
              </button>
            ))}
          </div>
        </div>

        <div className="card-surface rounded-2xl overflow-hidden" data-testid="perf-table">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Ranking media fantavoto stagionale</div>
              <div className="display font-black text-lg">{rows.length} calciatori</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 hidden md:block">
              Ordinati per <b className="text-slate-600">media fantavoto</b> · presenze · nome
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="px-3 py-3 text-left w-12">#</th>
                  <th className="px-3 py-3 text-left">Calciatore</th>
                  <th className="px-3 py-3 text-center w-16">Ruolo</th>
                  <th className="px-3 py-3 text-left">Squadra</th>
                  <th className="px-3 py-3 text-center w-16" title="Presenze / partite giocate">PG</th>
                  <th className="px-3 py-3 text-right w-28">Media fanta</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan="6" className="px-3 py-8 text-center text-slate-400 italic">Nessun calciatore corrisponde ai filtri.</td></tr>
                )}
                {rows.map((p, i) => {
                  const pg = p.matches_played || 0;
                  const trophy = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
                  return (
                    <tr key={`${p.name}-${p.role}-${i}`} className="border-t border-slate-100 hover:bg-slate-50"
                        data-testid={`perf-row-${i}`}>
                      <td className="px-3 py-2 text-slate-500 font-bold tabular-nums align-middle">
                        <div className="flex flex-col items-center leading-none gap-0.5">
                          {trophy && <span className="text-base leading-none">{trophy}</span>}
                          <span className="text-xs leading-none">{i + 1}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-900 font-semibold">
                        <button type="button" onClick={() => setSelectedPlayer({ name: p.name, role: p.role })}
                                className="hover:text-indigo-600 hover:underline decoration-dotted transition-colors text-left"
                                data-testid={`perf-name-${i}`}>
                          {p.name}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="text-[10px] font-black px-2 py-0.5 rounded" style={{ background: `${ROLE_COLORS[p.role]}22`, color: ROLE_COLORS[p.role] }}>{p.role}</span>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        <div className="flex flex-wrap gap-1.5" data-testid={`perf-teams-${i}`}>
                          {(p.teams || []).length === 0 && <span className="text-slate-400 italic text-xs">—</span>}
                          {(p.teams || []).map((tm, j) => (
                            <span key={`${tm.team_name}-${j}`}
                                  className="inline-flex items-center gap-1 px-1 sm:px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold"
                                  title={tm.team_name}>
                              {tm.girone && <GironeBadge girone={tm.girone} size={14} />}
                              <span className="hidden sm:inline truncate max-w-[180px]">{tm.team_name}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center tabular-nums font-semibold text-slate-700" data-testid={`perf-pg-${i}`}>
                        {pg > 0 ? pg : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-black tabular-nums" style={{ color: p.rating != null ? ratingColor(p.rating) : "#cbd5e1" }}>
                        {p.rating != null ? p.rating.toFixed(2) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Modal storico calciatore */}
      <Dialog open={!!selectedPlayer} onOpenChange={(o) => { if (!o) { setSelectedPlayer(null); setHistory(null); } }}>
        <DialogContent className="max-w-3xl bg-white" data-testid="perf-history-modal">
          <DialogHeader>
            <DialogTitle className="display font-black text-2xl">
              {selectedPlayer?.name}
              <span className="ml-2 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded"
                    style={{ background: `${ROLE_COLORS[selectedPlayer?.role]}22`, color: ROLE_COLORS[selectedPlayer?.role] }}>
                {selectedPlayer?.role}
              </span>
            </DialogTitle>
          </DialogHeader>
          {historyLoading && <div className="py-8 text-center text-slate-400">Caricamento storico…</div>}
          {history && !historyLoading && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3" data-testid="perf-history-stats">
                <StatMini label="Presenze" value={history.stats.played} />
                <StatMini label="Media fv" value={history.stats.avg_fantavoto?.toFixed(2) || "—"} color="#f97316" />
                <StatMini label="Gol" value={history.stats.goals} color="#16a34a" />
                <StatMini label="Assist" value={history.stats.assists} color="#0ea5e9" />
                <StatMini label="Gialli" value={history.stats.yellows} color="#eab308" />
                <StatMini label="Rossi" value={history.stats.reds} color="#dc2626" />
              </div>
              <div className="max-h-96 overflow-y-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                    <tr>
                      <th className="px-3 py-2 text-left">Gio</th>
                      <th className="px-3 py-2 text-left">Squadra</th>
                      <th className="px-3 py-2 text-center">Titolare</th>
                      <th className="px-3 py-2 text-center">Voto</th>
                      <th className="px-3 py-2 text-center">Fanta</th>
                      <th className="px-3 py-2 text-left">Eventi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.matches.length === 0 && (
                      <tr><td colSpan="6" className="px-3 py-8 text-center text-slate-400 italic">Nessuna partita giocata.</td></tr>
                    )}
                    {history.matches.map((m, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`history-row-${i}`}>
                        <td className="px-3 py-2 tabular-nums font-bold">G{m.matchday}</td>
                        <td className="px-3 py-2 text-slate-700">
                          <span className="inline-flex items-center gap-1">
                            {m.girone && <GironeBadge girone={m.girone} size={14} />}
                            <span className="truncate">{m.team_name}</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center text-[10px] font-bold">{m.starter ? "SI" : "NO"}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{m.voto_base ?? "—"}</td>
                        <td className="px-3 py-2 text-center tabular-nums font-black" style={{ color: m.fantavoto != null ? ratingColor(m.fantavoto) : "#cbd5e1" }}>
                          {m.fantavoto != null ? m.fantavoto.toFixed(1) : "—"}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(m.events || []).map((e, j) => <EventIcon key={j} type={e} />)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}

function StatMini({ label, value, color }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-2 py-2 text-center">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="display font-black text-lg tabular-nums" style={{ color: color || "#0f172a" }}>{value}</div>
    </div>
  );
}

const EVENT_MAP = {
  goal: { emoji: "⚽", label: "Gol", color: "bg-emerald-100 text-emerald-700" },
  own_goal: { emoji: "🥅", label: "Autogol", color: "bg-red-100 text-red-700" },
  goal_conceded: { emoji: "🛑", label: "Gol subito", color: "bg-slate-100 text-slate-700" },
  assist: { emoji: "🎯", label: "Assist", color: "bg-cyan-100 text-cyan-700" },
  yellow: { emoji: "🟨", label: "Giallo", color: "bg-yellow-100 text-yellow-800" },
  red: { emoji: "🟥", label: "Rosso", color: "bg-red-100 text-red-700" },
  penalty_scored: { emoji: "✅", label: "Rig. segnato", color: "bg-emerald-100 text-emerald-700" },
  penalty_missed: { emoji: "❌", label: "Rig. sbagliato", color: "bg-red-100 text-red-700" },
  penalty_saved: { emoji: "🧤", label: "Rig. parato", color: "bg-blue-100 text-blue-700" },
  captain: { emoji: "🅒", label: "Capitano", color: "bg-blue-100 text-blue-700" },
};

function EventIcon({ type }) {
  const cfg = EVENT_MAP[type] || { emoji: "•", label: type, color: "bg-slate-100 text-slate-700" };
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${cfg.color}`}
          title={cfg.label}>
      <span>{cfg.emoji}</span>
    </span>
  );
}
